import useAPI, {
  useAPIMutation,
  useDistinctFilterOptions
} from "@/services/api/hook";
import { useQueryClient } from "@tanstack/react-query";
import {
  APIObject,
  ExtendedUseQueryResult,
  Filters,
  SearchQuery
} from "@/services/api/types";
import {
  LeadData,
  LeadDealSuggestion,
  LeadDisqualifiedCompanyReasonsResponse,
  LeadInboundCampaignTypesResponse,
  LeadOutboundCampaignTypesResponse,
  LeadSourcesResponse,
  LeadsPage,
  LeadTypesResponse
} from "./types";
import {
  DisqualifiedReasonsResponse,
  ClosedAsQualifiedReasonsResponse
} from "@/entities/settings/types";
import { refreshEntityDetail, useEntities } from "@/services/api/entityhooks";
import { ComputedFields } from "@/services/api/types";
import { EntitySliceState } from "@/services/api/entitystore";
import useBoundStore, {
  MAX_ENTITIES_PER_PAGE
} from "@/services/store/BoundStore";
import { useEffect } from "react";
import { createUseFilteredEntitiesInfinite } from "@/services/api/infinite";

export const MAX_LEADS_PER_PAGE = MAX_ENTITIES_PER_PAGE;

export const useLeads = (
  ids: string[],
  options: {
    enabled?: boolean;
    computed_fields?: ComputedFields;
    forceRefetch?: boolean;
    include_additionals?: boolean;
  } = {}
) => {
  const useLeadsStore = (): EntitySliceState<LeadData> => {
    return useBoundStore((state) => state.leads);
  };

  return useEntities<LeadData>(
    ids,
    "leads",
    useLeadsStore,
    (endpoint, params, apiOptions) =>
      useAPI(
        endpoint,
        {
          ...params,
          queryParams: {
            ...(options.computed_fields && {
              computed_fields: options.computed_fields
            }),
            ...(options.include_additionals && {
              include_additionals: "true"
            })
          }
        },
        { ...apiOptions, enabled: options.enabled }
      ),
    undefined,
    options.computed_fields,
    options.forceRefetch
  );
};

export const useAllLeads = (
  page: number = 1,
  size: number = MAX_LEADS_PER_PAGE
): ExtendedUseQueryResult<LeadsPage> => {
  const result = useAPI("leads/all", {
    queryParams: { page: page.toString(), size: size.toString() }
  });

  const addLead = useBoundStore((state) => state.leads.addEntity);

  useEffect(() => {
    if (result.data?.leads) {
      result.data.leads.forEach(addLead);
    }
  }, [result.data, addLead]);

  return result;
};

export const {
  useFilteredEntitiesInfinite,
  useResetFilteredEntitiesInfinite: useResetLeadsInfinite
} = createUseFilteredEntitiesInfinite<LeadData>("leads", MAX_LEADS_PER_PAGE);

export const useLeadsInfinite = (
  filters?: Filters,
  search?: SearchQuery,
  groupedBy?: keyof LeadData,
  pageSize = MAX_LEADS_PER_PAGE,
  sortField?: string,
  sortAsc?: boolean,
  fetchAll?: boolean,
  enabled = true,
  computed_fields: ComputedFields = ComputedFields.ALL
) => {
  return useFilteredEntitiesInfinite(
    () => ({ addEntity: useBoundStore((state) => state.leads.addEntity) }),
    {
      filters,
      search,
      groupedBy,
      pageSize,
      sortField,
      sortAsc,
      fetchAll,
      enabled,
      computed_fields
    }
  );
};

export const useLeadSources = (): ExtendedUseQueryResult<LeadSourcesResponse> =>
  useAPI("leads/sources");

export const useLeadsDistinctFilterOptions = (
  fieldName: string,
  filters?: Filters,
  searchTerm?: string
) => {
  return useDistinctFilterOptions("leads", fieldName, filters, searchTerm);
};

export interface DuplicateLead {
  id: string | null;
  name: string | null;
  status: string | null;
}

export interface LeadDuplicateCheckResponse {
  leads: DuplicateLead[];
}

export const useCheckLeadDuplicates = (
  email: string,
  enabled: boolean = true
): ExtendedUseQueryResult<LeadDuplicateCheckResponse> => {
  const trimmedEmail = email.trim();
  return useAPI(
    "leads/check-duplicates",
    {
      method: "POST",
      data: { email: trimmedEmail }
    },
    { enabled: enabled && trimmedEmail.length > 0 }
  );
};

export interface CreateLeadWithDreamerRequest {
  email: string;
  source: number;
  productId: string;
  sdrOwner: string;
  aeOwner?: string | null;
  partnerId?: string | null;
  salesExplorationMeetingDate?: string | null;
}

/** Resolves on 202, before the lead exists — no store update; the outcome arrives as a Dreamer notification. */
export const useCreateLeadWithDreamerMutation = () =>
  useAPIMutation<CreateLeadWithDreamerRequest, { email: string }>(
    "createLeadWithDreamer",
    { method: "POST" }
  );

export const useCreateLeadMutation = () => {
  const addLead = useBoundStore((state) => state.leads.addEntity);
  return useAPIMutation<LeadData, APIObject>(
    "createLead",
    {
      method: "POST"
    },
    (response, data) => {
      addLead({
        ...data,
        id: response.id
      } as LeadData);
    }
  );
};

export const useUpdateLeadStatusMutation = () => {
  const updateLead = useBoundStore((state) => state.leads.updateEntity);
  const queryClient = useQueryClient();
  return useAPIMutation<
    {
      pathParams: {
        leadId: string;
      };
      data: {
        status: number;
        disqualifiedReason?: number;
        disqualifiedCompanyReason?: number;
        closedAsQualifiedReason?: number;
        disqualifyCompany?: boolean;
        disqualifyPerson?: boolean;
        statusNotes?: string;
      };
    },
    LeadData
  >(
    "leads/updateStatus",
    {
      method: "PUT"
    },
    (response) => {
      updateLead(response.id, response);
      refreshEntityDetail(queryClient, "leads", response.id);
    }
  );
};

export const useUnMarkLeadAsStuckMutation = () => {
  const updateLead = useBoundStore((state) => state.leads.updateEntity);
  return useAPIMutation<
    {
      pathParams: {
        leadId: string;
      };
      data: {};
    },
    LeadData
  >(
    "leads/unMarkAsStuck",
    {
      method: "PUT"
    },
    (response) => {
      updateLead(response.id, response);
    }
  );
};

export const useUpdateLeadMutation = () => {
  const updateLead = useBoundStore((state) => state.leads.updateEntity);
  const queryClient = useQueryClient();
  return useAPIMutation<
    { pathParams: { leadId: string }; data: LeadData },
    LeadData
  >(
    "leads/id",
    {
      method: "PUT"
    },
    (response, data) => {
      updateLead(response.id, data as Partial<LeadData>);
      refreshEntityDetail(queryClient, "leads", response.id);
    }
  );
};

export const useDeleteLeadMutation = () => {
  const removeLead = useBoundStore((state) => state.leads.removeEntity);
  return useAPIMutation<{ pathParams: { leadId: string } }, void>(
    "leads/id",
    {
      method: "DELETE"
    },
    (_response, data) => {
      removeLead(data.pathParams.leadId);
    }
  );
};

export const useDisqualifiedReasons =
  (): ExtendedUseQueryResult<DisqualifiedReasonsResponse> =>
    useAPI("settings/leads/disqualifiedReasons");

export const useClosedAsQualifiedReasons =
  (): ExtendedUseQueryResult<ClosedAsQualifiedReasonsResponse> =>
    useAPI("settings/leads/closedAsQualifiedReasons");

export const useDisqualifiedCompanyReasons =
  (): ExtendedUseQueryResult<LeadDisqualifiedCompanyReasonsResponse> =>
    useAPI("leads/disqualifiedCompanyReasons");

export const useLeadTypes = (): ExtendedUseQueryResult<LeadTypesResponse> => {
  const storedTypes = useBoundStore((state) => state.lookup.data.leadTypes);
  const setLeadTypes = useBoundStore((state) => state.lookup.setLeadTypes);

  const result = useAPI(
    "leads/types",
    {},
    {
      enabled: !storedTypes
    }
  );

  useEffect(() => {
    if (result.isSuccess && result.data?.leadsTypes && !storedTypes) {
      setLeadTypes(result.data.leadsTypes);
    }
  }, [result.data, result.isSuccess, setLeadTypes, storedTypes]);

  if (storedTypes) {
    return {
      ...result,
      data: { leadsTypes: storedTypes },
      isSuccess: true,
      isLoading: false,
      isFetching: false
    } as ExtendedUseQueryResult<LeadTypesResponse>;
  }

  return result;
};

export const useLeadInboundCampaignTypes =
  (): ExtendedUseQueryResult<LeadInboundCampaignTypesResponse> =>
    useAPI("settings/leads/inboundCampaignTypes");

export const useLeadOutboundCampaignTypes =
  (): ExtendedUseQueryResult<LeadOutboundCampaignTypesResponse> =>
    useAPI("settings/leads/outboundCampaignTypes");

// A lead without a suggestion answers 404, which is the normal "nothing to show" case.
export const useLeadDealSuggestion = (
  leadId: string,
  enabled: boolean
): ExtendedUseQueryResult<LeadDealSuggestion> =>
  useAPI(
    "leads/dealSuggestion",
    { method: "GET", pathParams: { leadId } },
    { enabled: enabled && !!leadId }
  );

export const useDismissLeadDealSuggestionMutation = () => {
  const queryClient = useQueryClient();
  // Refetch on failure too: a 409 means the suggestion already left the active
  // state elsewhere, and the refetch hides the banner.
  const refetchSuggestion = (
    _response: unknown,
    data: { pathParams: { leadId: string } }
  ) => {
    queryClient.invalidateQueries({
      queryKey: [
        "leads/dealSuggestion",
        { pathParams: { leadId: data.pathParams.leadId } }
      ]
    });
  };
  return useAPIMutation<
    { pathParams: { leadId: string }; data: Record<string, never> },
    LeadDealSuggestion
  >(
    "leads/dealSuggestionDismiss",
    { method: "POST" },
    refetchSuggestion,
    refetchSuggestion
  );
};

export default {
  useLeads,
  useAllLeads,
  useLeadsInfinite,
  useResetLeadsInfinite
};
